// dev/app-skeleton/src/lib/sync-client.ts
// 与 Worker 的 sync push / pull 客户端。
// D15: 新增 kickSync() 单飞包装 + startSyncLoop() 后台轮询，解 D14 "保存后 /api/sync/push 不触发" P0。
// 冲突保留：服务端 conflict → 客户端把"本地版"另存为新 entry（id'），UI 弹合并卡片。
import { aeadOpen, aeadSeal, buildAAD, bytesToBase64, base64ToBytes, zeroize } from "../crypto";
import { db, kvGet, kvSet } from "./db";
import { deriveFromRoot } from "./session-key";
const KV_LAST_SERVER_VER = "last_server_version";
/** 原生 Response.json() 在 TS 严格模式下返回 unknown；统一收口为类型断言一处。 */
async function jsonAs(r) {
    return (await r.json());
}
function csrfHeader() {
    const m = document.cookie.match(/(?:^|;\s*)csrf=([^;]+)/);
    return m ? { "X-CSRF-Token": decodeURIComponent(m[1]) } : {};
}
export async function pushPending(client_id, apiBase = "") {
    const pending = await db.ops.where("status").equals("pending").limit(50).toArray();
    if (!pending.length)
        return;
    await db.ops.bulkPut(pending.map((o) => ({ ...o, status: "in_flight" })));
    const items = pending.map((o) => ({
        op: o.op,
        entity_id: o.entity_id,
        base_version: o.base_version,
        payload: o.encrypted_blob ? { encrypted_b64: bytesToBase64(o.encrypted_blob) } : undefined,
        client_ts: o.client_ts,
    }));
    const r = await fetch(`${apiBase}/api/sync/push`, {
        method: "POST",
        credentials: "include",
        headers: { "content-type": "application/json", ...csrfHeader() },
        body: JSON.stringify({ client_id, items }),
    });
    if (!r.ok) {
        // 标回 pending；指数退避由 caller 决定
        await db.ops.bulkPut(pending.map((o) => ({ ...o, status: "pending", attempts: o.attempts + 1 })));
        throw new Error(`push failed: ${r.status}`);
    }
    const { results } = await jsonAs(r);
    for (const res of results) {
        const op = pending.find((p) => p.entity_id === res.entity_id);
        if (!op)
            continue;
        if (res.status === "applied") {
            await db.ops.delete(op.op_local_id);
            const entry = await db.entries.get(op.entity_id);
            if (entry) {
                await db.entries.put({ ...entry, local_known_server_version: res.server_version, dirty: false });
            }
        }
        else {
            // 冲突：标 conflict；caller 触发 pull + 弹合并卡片
            await db.ops.put({ ...op, status: "conflict" });
        }
    }
}
export async function pullSince(userId, apiBase = "") {
    let since = (await kvGet(KV_LAST_SERVER_VER)) ?? 0;
    let applied = 0;
    let conflicts = 0;
    while (true) {
        const r = await fetch(`${apiBase}/api/sync/pull?since=${since}&limit=200`, {
            method: "GET",
            credentials: "include",
        });
        if (!r.ok)
            throw new Error(`pull failed: ${r.status}`);
        const data = await jsonAs(r);
        for (const op of data.ops) {
            if (op.entity_kind === "entry") {
                const local = await db.entries.get(op.entity_id);
                if (op.op === "upsert_entry" && op.encrypted_b64) {
                    const blob = base64ToBytes(op.encrypted_b64);
                    // 冲突检测：本地有 pending 改动且服务端版本更高 → 标冲突
                    const pendingOp = await db.ops
                        .where("entity_id").equals(op.entity_id)
                        .filter((x) => x.status === "pending" || x.status === "in_flight" || x.status === "conflict")
                        .first();
                    if (pendingOp && local) {
                        conflicts++;
                        // 复制远端版到 entries（覆盖），本地版另存为新 entry（id'）
                        // SEC F-REV-01 同款修复：克隆时用 cloneId 的 entry_key 重新加密 + AAD 绑定 cloneId
                        const cloneId = crypto.randomUUID();
                        const cloneVersion = 1;
                        const oldKey = deriveFromRoot("entry", local.id);
                        const newKey = deriveFromRoot("entry", cloneId);
                        let resealed;
                        try {
                            const openVer = (local.local_known_server_version ?? 0) + 1;
                            const openAAD = buildAAD({
                                kind: "entry",
                                user_id: userId,
                                resource_id: local.id,
                                version: openVer,
                            });
                            const ptBytes = aeadOpen(oldKey, local.encrypted_blob, openAAD);
                            const sealAAD = buildAAD({
                                kind: "entry",
                                user_id: userId,
                                resource_id: cloneId,
                                version: cloneVersion,
                            });
                            resealed = aeadSeal(newKey, ptBytes, sealAAD).bytes;
                        }
                        finally {
                            zeroize(oldKey);
                            zeroize(newKey);
                        }
                        await db.entries.put({
                            ...local,
                            id: cloneId,
                            encrypted_blob: resealed,
                            dirty: true,
                            local_known_server_version: null,
                            updated_at: Date.now(),
                        });
                        // 标记 op 为 conflict
                        await db.ops.put({ ...pendingOp, status: "conflict" });
                    }
                    await db.entries.put({
                        id: op.entity_id,
                        encrypted_blob: blob,
                        // D14-DEV-v2 / SEC C' 修：去掉 plaintext_json 字段（明文不落 IDB）。
                        local_known_server_version: op.version,
                        dirty: false,
                        deleted: false,
                        created_at: local?.created_at ?? op.ts,
                        updated_at: op.ts,
                    });
                    applied++;
                }
                else if (op.op === "delete_entry") {
                    if (local)
                        await db.entries.put({ ...local, deleted: true, local_known_server_version: op.version });
                    applied++;
                }
                else if (op.op === "restore_entry") {
                    if (local)
                        await db.entries.put({ ...local, deleted: false, local_known_server_version: op.version });
                    applied++;
                }
            }
            // media 同理（略；media 上层负责）
        }
        since = data.next_cursor;
        await kvSet(KV_LAST_SERVER_VER, since);
        if (!data.has_more)
            break;
    }
    return { applied, conflicts };
}
export function getOrCreateClientId() {
    const k = "diary.client_id";
    let id = localStorage.getItem(k);
    if (!id) {
        id = crypto.randomUUID();
        localStorage.setItem(k, id);
    }
    return id;
}
// ===========================================================================
// D15 · sync runtime —— 单飞 kickSync + 后台轮询
// ===========================================================================
// 设计目标（解 D15 Task 1：保存后 Network 看不到 /api/sync/push）
//   - 任何写路径（entries.ts 的 upsertEntry/deleteEntry）保存成功后调一次 kickSync()
//   - 单飞：同一时刻最多 1 个 push 在跑（避免并发 push 把同一 op 双发）
//   - 401/未登录：静默吞掉（用户没登录时正常）
//   - 网络错：log warn，不抛（不破坏调用方 UI flow）
//   - 后台 loop：startSyncLoop() 每 30s 跑一次，覆盖"离线写 → 上线后没人 kick"场景
// ===========================================================================
let _kicking = false;
let _kickQueued = false;
/** 触发一次 push pending → server。单飞：同一时刻最多 1 个 in-flight；并发调用合并成 1 次回放。 */
export async function kickSync(apiBase = "") {
    if (_kicking) {
        _kickQueued = true;
        return;
    }
    _kicking = true;
    try {
        do {
            _kickQueued = false;
            try {
                await pushPending(getOrCreateClientId(), apiBase);
            }
            catch (e) {
                // 静默：401（未登录）/ 网络错 / 5xx 都不应破坏调用方（保存仍算成功，下一次 loop / kick 会重试）
                if (typeof console !== "undefined")
                    console.warn("[sync] kickSync error:", e);
            }
        } while (_kickQueued);
    }
    finally {
        _kicking = false;
    }
}
let _loopTimer = null;
/** 启动后台 sync loop（每 30s 跑一次）。在 App 根组件 mount 时调一次即可，重复调安全（idempotent）。 */
export function startSyncLoop(intervalMs = 30_000, apiBase = "") {
    if (_loopTimer)
        return () => { };
    _loopTimer = setInterval(() => { void kickSync(apiBase); }, intervalMs);
    // 首次立即跑一次，覆盖"刷新后立刻有 pending"场景
    void kickSync(apiBase);
    // 回前台时也跑一次，覆盖"切后台时离线写 → 回前台马上同步"
    if (typeof document !== "undefined") {
        document.addEventListener("visibilitychange", _onVisible);
    }
    return () => {
        if (_loopTimer) {
            clearInterval(_loopTimer);
            _loopTimer = null;
        }
        if (typeof document !== "undefined")
            document.removeEventListener("visibilitychange", _onVisible);
    };
}
function _onVisible() {
    if (typeof document !== "undefined" && document.visibilityState === "visible") {
        void kickSync();
    }
}
